import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { Skeleton } from '@listening/frontend/components/radix/skeleton';
import { LibrarySkeleton } from '@/components/web-fe/design-v2/skeletons';

/**
 * Provider-free dashboard loading shell, used as the `/app/_authenticated`
 * route's `pendingComponent`. That route's `beforeLoad` runs an async session
 * check (`getSession`); while it's pending the real dashboard chrome —
 * `SidebarV2` and `TopBarV2` — has NOT mounted yet (it lives in the
 * `_dashboard` layout, below this gate, behind several providers). So this
 * shell must avoid every dashboard hook/provider and mirror the layout with
 * static placeholders, so the first paint shows a sidebar + card-grid skeleton
 * instead of a full-screen spinner over an otherwise empty page.
 *
 * Once the session resolves, `_dashboard` mounts the real chrome and each route
 * swaps in its own skeleton (e.g. home's `HomePageSkeleton`, which reuses
 * `DashboardContentSkeleton` below so the grids line up across the swap).
 */
export function DashboardSkeleton() {
  return (
    <div className='flex size-full flex-col bg-[var(--v2-app-bg)] text-[var(--v2-text-primary)] subpixel-antialiased'>
      <div className='flex grow overflow-hidden'>
        <DashboardSidebarSkeleton />
        <main className='relative flex h-full min-w-0 grow flex-col overflow-hidden bg-[var(--v2-app-bg)]'>
          <TopBarSkeleton />
          <article className='relative flex h-full flex-col overflow-hidden'>
            <ScrollArea
              className='grow'
              viewPortClassName='bg-transparent'
              useFlexFix
            >
              <div className='mx-auto flex w-full max-w-[var(--v2-content-max)] flex-col gap-[var(--v2-gap-6)] px-[var(--v2-content-pad)] py-[var(--v2-gap-6)] pb-16'>
                <LibrarySkeleton />
              </div>
            </ScrollArea>
          </article>
        </main>
      </div>
    </div>
  );
}

/**
 * Static placeholder for `SidebarV2`, which is `lg`-only (smaller screens reach
 * it through the top bar's drawer), so this placeholder is too. Matches the
 * real sidebar width so the content column doesn't shift when it mounts.
 */
function DashboardSidebarSkeleton() {
  return (
    <div className='hidden h-full w-[var(--v2-sidebar-width)] shrink-0 lg:block'>
      <nav className='relative flex h-full min-h-0 flex-col gap-[var(--v2-gap-4)] overflow-hidden border-r border-r-[var(--v2-sidebar-border)] bg-[var(--v2-sidebar-bg)] p-[var(--v2-sidebar-padding)]'>
        <div className='px-2 pt-2 pb-1'>
          <Skeleton className='h-6 w-[126px]' />
        </div>
        <div className='flex flex-col gap-0.5'>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton
              key={`nav-item-skel-${String(index)}`}
              className='h-9 w-full rounded-[var(--v2-button-radius)]'
            />
          ))}
        </div>
      </nav>
    </div>
  );
}

/** Static placeholder matching `TopBarV2`'s shape (search field + actions). */
function TopBarSkeleton() {
  return (
    <header className='flex w-full items-center gap-[var(--v2-gap-3)] border-b border-b-[var(--v2-sidebar-border)] bg-[var(--v2-top-bar-bg)] px-[var(--v2-content-pad)] py-3'>
      <div className='flex min-w-0 flex-1'>
        <Skeleton className='h-11 w-full max-w-[480px] rounded-[var(--v2-button-radius)]' />
      </div>
      <div className='flex shrink-0 items-center gap-[var(--v2-gap-3)]'>
        <Skeleton className='h-10 w-[132px] rounded-[var(--v2-button-radius)]' />
        <Skeleton className='size-9 rounded-full' />
      </div>
    </header>
  );
}

/**
 * Re-exported so the pre-providers shell above and the route-level
 * `HomePageSkeleton` render the same markup, and the grid stays put across the
 * handoff between them.
 */
export { LibrarySkeleton as DashboardContentSkeleton } from '@/components/web-fe/design-v2/skeletons';
